import * as React from 'react'
import Dropzone from 'react-dropzone'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Loading, Text } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import { ImportContractsModalPageDocument } from 'generated/resources/graphql'
import _ from 'lodash'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { parseErrorData } from 'modules/core/lib/parseErrorData'
import type { ValidationErrorData } from 'modules/core/lib/parseErrorData'

import type { ContractImporterContextProps } from 'modules/employees/components/ContractImporter/contractsImporterContext'
import OverlimitEmployeesFootNote from 'modules/employees/components/OverlimitEmployees/FootNote'
import useProcessContractFile from 'modules/employees/hooks/useProcessContractFile'
import { FormActions } from 'modules/employees/pages/ImportContractsPage/components/ImportModeForm'
import LegalEntitySelect from 'modules/employees/pages/ImportContractsPage/components/LegalEntitySelect'

import DocumentText from './_DocumentText'
import InfoComponent from './_InfoComponent'

const EXPORT_PATH = '/contracts_importer/export'
const IMPORT_PATH = '/contracts_importer/import'
const CHECK_DOWNLOAD = '/contracts_importer/download_available'

interface Response {
  data: ValidationErrorData
}

interface Props {
  importer: ContractImporterContextProps
  onClose: () => void
}

class ContractsFileForm extends FactorialForm {
  constructor(values: { file: File | undefined | null }) {
    super(values, {
      file: 'file',
    })
  }
}

const ImportContractsFileSelector = ({ importer, onClose }: Props) => {
  const api = React.useRef(ApiClient()).current
  const initialFile = importer.files.length > 0 ? importer.files[0] : null
  const form = React.useRef(new ContractsFileForm({ file: initialFile })).current
  const fileField = form.get('file')
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    company: { id: companyId, subscription },
    access,
  } = useCurrentSession()

  const event =
    importer.mode === FormActions.export_only_reference
      ? trackingEvents.importContracts.selectImportCurrentContract
      : trackingEvents.importContracts.selectImportAllContractVersion
  const { data: importerData, loading } = useQuery(ImportContractsModalPageDocument, {
    variables: {
      companyId,
    },
  })
  const canImportEmployees =
    importerData?.onboardingImporter?._policies.importEmployees.can ?? false
  const legalEntities = importerData?.companies.legalEntities ?? []

  const options = legalEntities.map((le) => {
    return {
      label: le.legalName,
      value: le.id,
    }
  })

  let fileErrors

  useEffect(() => {
    track(event, {
      companyId: companyId,
      acessId: access.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { running, onSubmit, error, value } = useProcessContractFile({
    api: api,
    file: fileField.value,
    importPath: IMPORT_PATH,
  })

  if (error) {
    const genericError = i18n.t('employees.import_generic_error')
    const errors = (value as Response)?.data?.errors || [genericError]
    fileErrors = parseErrorData(errors)
  }

  const getName = () => {
    if (!fileField.value) return null

    if (_.isString(fileField.value)) return i18n.t('common.document')

    return fileField.value.name || fileField.value.filename
  }

  const onDropFile = (files: File[]) => {
    form.cleanAll()
    fileField.set(files[0])
  }

  const onCancel = () => {
    importer.setFiles([])
    navigate(navigation.employees.list.importContractsMode.fullPath())
  }

  if (!canImportEmployees) return <NotFound />
  if (loading) return <Loading />

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        illustration="group"
        title={i18n.t('import_contract.title')}
        description={i18n.t('import_contract.description')}
        onClose={onClose}
        onSubmit={onSubmit}
        footer={
          <>
            <Button onClick={onCancel} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={!fileField.value || running} submit>
              {i18n.t('import.process_file')}
            </Button>
          </>
        }
      >
        <Box gap="s24">
          <Box flexDirection="column" justifyContent="center" alignItems="center" gap="s16">
            <LegalEntitySelect
              options={options}
              legalEntities={legalEntities}
              importMode={importer.mode || FormActions.export_only_reference}
              downloadPath={EXPORT_PATH}
              allowEmptyDownload={false}
              checkDownload={CHECK_DOWNLOAD}
            />
          </Box>
          <Box gap="s8">
            <Text.Normal>{i18n.t('import_contract.upload_file')}</Text.Normal>
            <Dropzone onDrop={onDropFile} multiple={false}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <div {...getRootProps()}>
                  <Box
                    background={isDragActive ? 'viridian100' : 'grey000White'}
                    cursor="pointer"
                    justifyContent="center"
                    padding="s32"
                    border={{
                      all: {
                        width: 's2',
                        style: 'dashed',
                        color: 'grey400',
                      },
                    }}
                  >
                    <input {...getInputProps()} />
                    <DocumentText filename={getName()}>
                      {i18n.tView('import_contract.import_placeholder', {
                        browse: (
                          <Text.Normal color="viridian800">{i18n.t('import.browse')}</Text.Normal>
                        ),
                      })}
                    </DocumentText>
                  </Box>
                </div>
              )}
            </Dropzone>
          </Box>
          <InfoComponent errorMessage={fileErrors} />
          <OverlimitEmployeesFootNote />
        </Box>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(ImportContractsFileSelector)
